import { http } from '@/api/http';
import type { PageEnvelope } from '@/api/shared';

/**
 * Raw transport surface for the FHIR-integration *entry* CRUD (PTV2-1383): the per-tenant
 * `fhir_integration` records that carry the launch knobs, the FHIR service root URL, the encrypted
 * system-account credentials and the Auth0 M2M settings. Modelled on the admin email-server slice —
 * hand-written snake_case DTOs; the api-layer mapper normalises to camelCase view models.
 *
 * The six secret fields (`system_account_user`, `system_account_pass`, `auth0_client_id`,
 * `auth0_client_secret`, `smart_client_id`, `smart_client_secret`) are write-only: sent on
 * create/update, never returned. The matching `has_*` booleans tell the UI whether a value is
 * stored.
 *
 * `PUT` is a *full replace* of every non-secret editable field, so any field this client omits is
 * nulled server-side. Every editable field must therefore appear in `toDto` — see PTV2-1495.
 *
 * Paths are base-relative — `http` prepends `VITE_API_BASE_URL` which already carries `/api/v1`, so
 * these read as `/fhir-integrations/...`.
 */

/** Wire DTO for a FHIR integration entry. All fields optional on the wire; the mapper normalises. */
export interface FhirIntegrationDto {
  id?: string;
  name?: string;
  is_default?: boolean;
  active?: boolean;
  skip_authentication?: boolean;
  authorization_criteria?: string | null;
  patient_id_header_name?: string | null;
  access_token_header_name?: string | null;
  frontend_base_url?: string | null;
  patient_id_search_criteria?: string | null;
  patient_id_extension_url?: string | null;
  fhir_service_root_url?: string | null;
  /** Write-only — never present on reads. */
  system_account_user?: string | null;
  /** Read-only — whether a system-account username is stored. */
  has_system_account_user?: boolean;
  /** Write-only — never present on reads. */
  system_account_pass?: string | null;
  /** Read-only — whether a system-account password is stored. */
  has_system_account_pass?: boolean;
  auth0_token_url?: string | null;
  auth0_api_audience?: string | null;
  auth0_connection?: string | null;
  /** Write-only — never present on reads. */
  auth0_client_id?: string | null;
  /** Read-only — whether an Auth0 client id is stored. */
  has_auth0_client_id?: boolean;
  /** Write-only — never present on reads. */
  auth0_client_secret?: string | null;
  /** Read-only — whether an Auth0 client secret is stored. */
  has_auth0_client_secret?: boolean;
  authorization_host?: string | null;

  // ---- SMART EHR launch (PTV2-1490 / PTV2-1495) ----
  /** Write-only — never present on reads. From the EHR app registration. */
  smart_client_id?: string | null;
  /** Read-only — whether a SMART client id is stored. */
  has_smart_client_id?: boolean;
  /** Write-only — never present on reads. Absent ⇒ a public (PKCE-only) client. */
  smart_client_secret?: string | null;
  /** Read-only — whether a SMART client secret is stored. */
  has_smart_client_secret?: boolean;
  /** Must byte-match the EHR registration. Blank ⇒ the gateway derives `{scheme}://{host}/fhir/callback`. */
  smart_redirect_uri?: string | null;
  /** Space-delimited. Blank ⇒ the gateway's code default. */
  smart_scopes?: string | null;
}

export type FhirIntegrationPageDto = PageEnvelope<FhirIntegrationDto>;

const BASE = '/fhir-integrations';

function encode(id: string): string {
  return encodeURIComponent(id);
}

export const adminFhirIntegrationEntryEndpoints = {
  /** GET /fhir-integrations — paginated list of entries for the current tenant. */
  getFhirIntegrations: (params?: {
    page?: number;
    size?: number;
    active?: boolean;
    signal?: AbortSignal;
  }): Promise<FhirIntegrationPageDto> => {
    const search = new URLSearchParams();
    if (params?.page !== undefined) search.set('page', String(params.page));
    if (params?.size !== undefined) search.set('size', String(params.size));
    if (params?.active !== undefined) search.set('active', String(params.active));
    const query = search.toString();
    const path = query.length > 0 ? `${BASE}?${query}` : BASE;
    return http.get<FhirIntegrationPageDto>(
      path,
      params?.signal !== undefined ? { signal: params.signal } : undefined,
    );
  },

  /** GET /fhir-integrations/{id} — a single entry (for the per-integration detail view). */
  getFhirIntegration: (id: string, options?: { signal?: AbortSignal }): Promise<FhirIntegrationDto> =>
    http.get<FhirIntegrationDto>(`${BASE}/${encode(id)}`, options),

  /** POST /fhir-integrations — create. 409 if the name is taken. */
  createFhirIntegration: (
    body: FhirIntegrationDto,
    options?: { signal?: AbortSignal },
  ): Promise<FhirIntegrationDto> => http.post<FhirIntegrationDto>(BASE, body, options),

  /** PUT /fhir-integrations/{id} — update. A blank secret keeps the stored one. */
  updateFhirIntegration: (
    id: string,
    body: FhirIntegrationDto,
    options?: { signal?: AbortSignal },
  ): Promise<FhirIntegrationDto> => http.put<FhirIntegrationDto>(`${BASE}/${encode(id)}`, body, options),

  /** DELETE /fhir-integrations/{id}. */
  deleteFhirIntegration: (id: string, options?: { signal?: AbortSignal }): Promise<undefined> =>
    http.delete<undefined>(`${BASE}/${encode(id)}`, options),
};
